import { createFileRoute } from "@tanstack/react-router";
import { useState, useCallback, useEffect, useMemo } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  Loader2,
  FolderTree,
  ClipboardPaste,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { myappApi } from "@/services/api/myapp";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import type { AreaSet, AreaSetDetail, AreaSetArea, AreaSetSubArea } from "@/types/myapp";

export const Route = createFileRoute("/portal/myapp/areas")({
  component: AreasPage,
});

function AreasPage() {
  const { token } = useAuth();
  const { can } = usePermissions();
  const canEditAreas = can("edit", "tables");
  const canDeleteAreas = can("delete", "tables");
  const [areaSets, setAreaSets] = useState<AreaSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedSetId, setExpandedSetId] = useState<string | null>(null);
  const [expandedDetail, setExpandedDetail] = useState<AreaSetDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // AreaSet CRUD state
  const [showCreateSet, setShowCreateSet] = useState(false);
  const [showEditSet, setShowEditSet] = useState(false);
  const [showDeleteSet, setShowDeleteSet] = useState(false);
  const [editingSet, setEditingSet] = useState<AreaSet | null>(null);
  const [setName, setSetName] = useState("");
  const [setDescription, setSetDescription] = useState("");
  const [saving, setSaving] = useState(false);

  // Area CRUD state
  const [showCreateArea, setShowCreateArea] = useState(false);
  const [showEditArea, setShowEditArea] = useState(false);
  const [showDeleteArea, setShowDeleteArea] = useState(false);
  const [editingArea, setEditingArea] = useState<AreaSetArea | null>(null);
  const [areaName, setAreaName] = useState("");

  // SubArea CRUD state
  const [showCreateSubArea, setShowCreateSubArea] = useState(false);
  const [showEditSubArea, setShowEditSubArea] = useState(false);
  const [showDeleteSubArea, setShowDeleteSubArea] = useState(false);
  const [parentAreaForSub, setParentAreaForSub] = useState<AreaSetArea | null>(null);
  const [editingSubArea, setEditingSubArea] = useState<AreaSetSubArea | null>(null);
  const [subAreaName, setSubAreaName] = useState("");

  // Paste state
  const [showPasteAreas, setShowPasteAreas] = useState(false);
  const [showPasteSubAreas, setShowPasteSubAreas] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasting, setPasting] = useState(false);

  // Expanded areas within a set
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set());

  const fetchAreaSets = useCallback(async () => {
    try {
      const data = await myappApi.areaSets.list(token);
      setAreaSets(data);
    } catch {
      toast.error("Failed to load area sets");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAreaSets();
  }, [fetchAreaSets]);

  const fetchDetail = useCallback(async (areaSetId: string) => {
    setLoadingDetail(true);
    try {
      const detail = await myappApi.areaSets.get(token, areaSetId);
      setExpandedDetail(detail);
    } catch {
      toast.error("Failed to load area set details");
    } finally {
      setLoadingDetail(false);
    }
  }, [token]);

  const toggleSet = (setId: string) => {
    if (expandedSetId === setId) {
      setExpandedSetId(null);
      setExpandedDetail(null);
    } else {
      setExpandedSetId(setId);
      fetchDetail(setId);
    }
  };

  const toggleArea = (areaId: string) => {
    setExpandedAreas((prev) => {
      const next = new Set(prev);
      if (next.has(areaId)) next.delete(areaId);
      else next.add(areaId);
      return next;
    });
  };

  // ── AreaSet handlers ─────────────────────────────────────────────────

  const handleCreateSet = async () => {
    if (!setName.trim()) return;
    setSaving(true);
    try {
      await myappApi.areaSets.create(token, {
        name: setName.trim(),
        description: setDescription.trim() || null,
      });
      toast.success(`Area set "${setName.trim()}" created`);
      setShowCreateSet(false);
      setSetName("");
      setSetDescription("");
      fetchAreaSets();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create area set");
    } finally {
      setSaving(false);
    }
  };

  const handleEditSet = async () => {
    if (!editingSet || !setName.trim()) return;
    setSaving(true);
    try {
      await myappApi.areaSets.update(token, editingSet.id, {
        name: setName.trim(),
        description: setDescription.trim() || null,
      });
      toast.success("Area set updated");
      setShowEditSet(false);
      setEditingSet(null);
      fetchAreaSets();
      if (expandedSetId === editingSet.id) fetchDetail(editingSet.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update area set");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSet = async () => {
    if (!editingSet) return;
    setSaving(true);
    try {
      await myappApi.areaSets.delete(token, editingSet.id);
      toast.success(`Area set "${editingSet.name}" deleted`);
      setShowDeleteSet(false);
      setEditingSet(null);
      if (expandedSetId === editingSet.id) {
        setExpandedSetId(null);
        setExpandedDetail(null);
      }
      fetchAreaSets();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete area set");
    } finally {
      setSaving(false);
    }
  };

  // ── Area handlers ────────────────────────────────────────────────────

  const handleCreateArea = async () => {
    if (!expandedSetId || !areaName.trim()) return;
    setSaving(true);
    try {
      await myappApi.areaSets.createArea(token, expandedSetId, {
        name: areaName.trim(),
      });
      toast.success(`Area "${areaName.trim()}" created`);
      setShowCreateArea(false);
      setAreaName("");
      fetchDetail(expandedSetId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create area");
    } finally {
      setSaving(false);
    }
  };

  const handleEditArea = async () => {
    if (!expandedSetId || !editingArea || !areaName.trim()) return;
    setSaving(true);
    try {
      await myappApi.areaSets.updateArea(token, expandedSetId, editingArea.id, {
        name: areaName.trim(),
      });
      toast.success("Area updated");
      setShowEditArea(false);
      setEditingArea(null);
      fetchDetail(expandedSetId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update area");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteArea = async () => {
    if (!expandedSetId || !editingArea) return;
    setSaving(true);
    try {
      await myappApi.areaSets.deleteArea(token, expandedSetId, editingArea.id);
      toast.success(`Area "${editingArea.name}" deleted`);
      setShowDeleteArea(false);
      setEditingArea(null);
      fetchDetail(expandedSetId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete area");
    } finally {
      setSaving(false);
    }
  };

  // ── SubArea handlers ─────────────────────────────────────────────────

  const handleCreateSubArea = async () => {
    if (!expandedSetId || !parentAreaForSub || !subAreaName.trim()) return;
    setSaving(true);
    try {
      await myappApi.areaSets.createSubArea(
        token,
        expandedSetId,
        parentAreaForSub.id,
        { name: subAreaName.trim() },
      );
      toast.success(`Sub-area "${subAreaName.trim()}" created`);
      setShowCreateSubArea(false);
      setSubAreaName("");
      setParentAreaForSub(null);
      fetchDetail(expandedSetId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create sub-area");
    } finally {
      setSaving(false);
    }
  };

  const handleEditSubArea = async () => {
    if (!expandedSetId || !parentAreaForSub || !editingSubArea || !subAreaName.trim()) return;
    setSaving(true);
    try {
      await myappApi.areaSets.updateSubArea(
        token,
        expandedSetId,
        parentAreaForSub.id,
        editingSubArea.id,
        { name: subAreaName.trim() },
      );
      toast.success("Sub-area updated");
      setShowEditSubArea(false);
      setEditingSubArea(null);
      setParentAreaForSub(null);
      fetchDetail(expandedSetId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update sub-area");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSubArea = async () => {
    if (!expandedSetId || !parentAreaForSub || !editingSubArea) return;
    setSaving(true);
    try {
      await myappApi.areaSets.deleteSubArea(
        token,
        expandedSetId,
        parentAreaForSub.id,
        editingSubArea.id,
      );
      toast.success(`Sub-area "${editingSubArea.name}" deleted`);
      setShowDeleteSubArea(false);
      setEditingSubArea(null);
      setParentAreaForSub(null);
      fetchDetail(expandedSetId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete sub-area");
    } finally {
      setSaving(false);
    }
  };

  // ── Paste handlers ──────────────────────────────────────────────────

  const parseLines = (text: string) =>
    text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

  const parsedLines = useMemo(() => parseLines(pasteText), [pasteText]);

  const handlePasteAreas = async () => {
    if (!expandedSetId) return;
    const names = parsedLines;
    if (names.length === 0) return;

    setPasting(true);
    let created = 0;
    let skipped = 0;
    for (const name of names) {
      try {
        await myappApi.areaSets.createArea(token, expandedSetId, { name });
        created++;
      } catch {
        skipped++;
      }
    }
    if (created > 0) {
      const parts = [`${created} area${created !== 1 ? "s" : ""} created`];
      if (skipped > 0) parts.push(`${skipped} skipped (already exist)`);
      toast.success(parts.join(", "));
      fetchDetail(expandedSetId);
    } else if (skipped > 0) {
      toast.info(`All ${skipped} area${skipped !== 1 ? "s" : ""} already exist`);
    }
    setShowPasteAreas(false);
    setPasteText("");
    setPasting(false);
  };

  const handlePasteSubAreas = async () => {
    if (!expandedSetId || !parentAreaForSub) return;
    const names = parsedLines;
    if (names.length === 0) return;

    setPasting(true);
    let created = 0;
    let skipped = 0;
    for (const name of names) {
      try {
        await myappApi.areaSets.createSubArea(
          token,
          expandedSetId,
          parentAreaForSub.id,
          { name },
        );
        created++;
      } catch {
        skipped++;
      }
    }
    if (created > 0) {
      const parts = [`${created} sub-area${created !== 1 ? "s" : ""} created`];
      if (skipped > 0) parts.push(`${skipped} skipped (already exist)`);
      toast.success(parts.join(", "));
      fetchDetail(expandedSetId);
    } else if (skipped > 0) {
      toast.info(`All ${skipped} sub-area${skipped !== 1 ? "s" : ""} already exist`);
    }
    setShowPasteSubAreas(false);
    setPasteText("");
    setParentAreaForSub(null);
    setPasting(false);
  };

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Areas</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Define reusable sets of areas and sub-areas. When you create a versioned or
            latest table, you can link it to an area set to categorise your data by
            department, team, or project.
          </p>
        </div>
        {canEditAreas && (
          <Button onClick={() => { setSetName(""); setSetDescription(""); setShowCreateSet(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            New Area Set
          </Button>
        )}
      </div>

      {/* Loading state */}
      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading area sets...
        </div>
      ) : areaSets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FolderTree className="h-12 w-12 text-muted-foreground mb-4" />
            <CardTitle className="text-lg mb-2">No area sets yet</CardTitle>
            <CardDescription className="text-center max-w-md mb-4">
              Area sets let you define a reusable hierarchy of areas and sub-areas.
              For example, create a "Budgeting Areas" set with areas like "HR",
              "Sales", and "Production", each with their own sub-areas.
            </CardDescription>
            {canEditAreas && (
              <Button onClick={() => { setSetName(""); setSetDescription(""); setShowCreateSet(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Create your first area set
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {areaSets.map((areaSet) => (
            <Card key={areaSet.id}>
              <div
                className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => toggleSet(areaSet.id)}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {expandedSetId === areaSet.id ? (
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0" />
                  )}
                  <FolderTree className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="font-medium truncate">{areaSet.name}</span>
                  {areaSet.description && (
                    <span className="text-xs text-muted-foreground truncate ml-2">
                      {areaSet.description}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                  {canEditAreas && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => {
                        setEditingSet(areaSet);
                        setSetName(areaSet.name);
                        setSetDescription(areaSet.description || "");
                        setShowEditSet(true);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {canDeleteAreas && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => {
                        setEditingSet(areaSet);
                        setShowDeleteSet(true);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Expanded detail */}
              {expandedSetId === areaSet.id && (
                <div className="border-t px-4 py-3">
                  {loadingDetail ? (
                    <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Loading areas...
                    </div>
                  ) : expandedDetail ? (
                    <div className="space-y-2">
                      {expandedDetail.areas.length === 0 && (
                        <p className="text-xs text-muted-foreground py-1">
                          No areas yet. Add areas to build your categorisation hierarchy.
                        </p>
                      )}
                      {expandedDetail.areas.length > 0 &&
                        expandedDetail.areas.map((area) => (
                          <Collapsible
                            key={area.id}
                            open={expandedAreas.has(area.id)}
                            onOpenChange={() => toggleArea(area.id)}
                          >
                            <div className="flex items-center justify-between py-1">
                              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                <CollapsibleTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
                                    {expandedAreas.has(area.id) ? (
                                      <ChevronDown className="h-3 w-3" />
                                    ) : (
                                      <ChevronRight className="h-3 w-3" />
                                    )}
                                  </Button>
                                </CollapsibleTrigger>
                                <span className="text-sm font-medium truncate">{area.name}</span>
                                <span className="text-xs text-muted-foreground shrink-0">
                                  ({area.sub_areas?.length ?? 0} sub-areas)
                                </span>
                              </div>
                              <div className="flex items-center gap-0.5 shrink-0">
                                {canEditAreas && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                      setEditingArea(area);
                                      setAreaName(area.name);
                                      setShowEditArea(true);
                                    }}
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                )}
                                {canDeleteAreas && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                    onClick={() => {
                                      setEditingArea(area);
                                      setShowDeleteArea(true);
                                    }}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </div>

                            <CollapsibleContent>
                              <div className="ml-8 space-y-1 pb-2">
                                {area.sub_areas && area.sub_areas.length > 0 ? (
                                  area.sub_areas.map((subArea) => (
                                    <div
                                      key={subArea.id}
                                      className="flex items-center justify-between rounded border bg-muted/50 px-2 py-1"
                                    >
                                      <span className="text-xs font-medium">{subArea.name}</span>
                                      <div className="flex gap-0.5">
                                        {canEditAreas && (
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-5 w-5"
                                            onClick={() => {
                                              setParentAreaForSub(area);
                                              setEditingSubArea(subArea);
                                              setSubAreaName(subArea.name);
                                              setShowEditSubArea(true);
                                            }}
                                          >
                                            <Pencil className="h-2.5 w-2.5" />
                                          </Button>
                                        )}
                                        {canDeleteAreas && (
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-5 w-5 text-muted-foreground hover:text-destructive"
                                            onClick={() => {
                                              setParentAreaForSub(area);
                                              setEditingSubArea(subArea);
                                              setShowDeleteSubArea(true);
                                            }}
                                          >
                                            <Trash2 className="h-2.5 w-2.5" />
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-xs text-muted-foreground">No sub-areas yet.</p>
                                )}
                                {canEditAreas && (
                                  <div className="flex gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 text-xs"
                                      onClick={() => {
                                        setParentAreaForSub(area);
                                        setSubAreaName("");
                                        setShowCreateSubArea(true);
                                      }}
                                    >
                                      <Plus className="h-3 w-3 mr-1" />
                                      Add Sub-area
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 text-xs"
                                      onClick={() => {
                                        setParentAreaForSub(area);
                                        setPasteText("");
                                        setShowPasteSubAreas(true);
                                      }}
                                    >
                                      <ClipboardPaste className="h-3 w-3 mr-1" />
                                      Paste Sub-areas
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        ))
                      }

                      {canEditAreas && (
                        <div className="flex gap-2 mt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setAreaName("");
                              setShowCreateArea(true);
                            }}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Area
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setPasteText("");
                              setShowPasteAreas(true);
                            }}
                          >
                            <ClipboardPaste className="h-3 w-3 mr-1" />
                            Paste Areas
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* ── AreaSet Dialogs ──────────────────────────────────────────────── */}

      {/* Create AreaSet */}
      <Dialog open={showCreateSet} onOpenChange={setShowCreateSet}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>New Area Set</DialogTitle>
            <DialogDescription>
              Create a named collection of areas and sub-areas that tables can use.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input
                value={setName}
                onChange={(e) => setSetName(e.target.value)}
                placeholder="e.g. Budgeting Areas"
                onKeyDown={(e) => { if (e.key === "Enter" && setName.trim()) handleCreateSet(); }}
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={setDescription}
                onChange={(e) => setSetDescription(e.target.value)}
                placeholder="What are these areas used for?"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateSet(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleCreateSet} disabled={saving || !setName.trim()}>
              {saving ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit AreaSet */}
      <Dialog open={showEditSet} onOpenChange={setShowEditSet}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Edit Area Set</DialogTitle>
            <DialogDescription>Update area set details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input
                value={setName}
                onChange={(e) => setSetName(e.target.value)}
                placeholder="Area set name"
                onKeyDown={(e) => { if (e.key === "Enter" && setName.trim()) handleEditSet(); }}
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={setDescription}
                onChange={(e) => setSetDescription(e.target.value)}
                placeholder="What are these areas used for?"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditSet(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleEditSet} disabled={saving || !setName.trim()}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete AreaSet */}
      <AlertDialog open={showDeleteSet} onOpenChange={setShowDeleteSet}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Area Set</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{editingSet?.name}&rdquo;?
              This will also delete all its areas and sub-areas. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSet}
              disabled={saving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {saving ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Area Dialogs ─────────────────────────────────────────────────── */}

      {/* Create Area */}
      <Dialog open={showCreateArea} onOpenChange={setShowCreateArea}>
        <DialogContent className="sm:max-w-[350px]">
          <DialogHeader>
            <DialogTitle>New Area</DialogTitle>
            <DialogDescription>Add an area to this set.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Name</Label>
            <Input
              value={areaName}
              onChange={(e) => setAreaName(e.target.value)}
              placeholder="e.g. Human Resources"
              onKeyDown={(e) => { if (e.key === "Enter" && areaName.trim()) handleCreateArea(); }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateArea(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleCreateArea} disabled={saving || !areaName.trim()}>
              {saving ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Area */}
      <Dialog open={showEditArea} onOpenChange={setShowEditArea}>
        <DialogContent className="sm:max-w-[350px]">
          <DialogHeader>
            <DialogTitle>Edit Area</DialogTitle>
            <DialogDescription>Update area name.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Name</Label>
            <Input
              value={areaName}
              onChange={(e) => setAreaName(e.target.value)}
              placeholder="Area name"
              onKeyDown={(e) => { if (e.key === "Enter" && areaName.trim()) handleEditArea(); }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditArea(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleEditArea} disabled={saving || !areaName.trim()}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Area */}
      <AlertDialog open={showDeleteArea} onOpenChange={setShowDeleteArea}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Area</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{editingArea?.name}&rdquo;?
              This will also delete all its sub-areas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteArea}
              disabled={saving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {saving ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── SubArea Dialogs ──────────────────────────────────────────────── */}

      {/* Create SubArea */}
      <Dialog open={showCreateSubArea} onOpenChange={setShowCreateSubArea}>
        <DialogContent className="sm:max-w-[350px]">
          <DialogHeader>
            <DialogTitle>New Sub-area</DialogTitle>
            <DialogDescription>
              Add a sub-area to &ldquo;{parentAreaForSub?.name}&rdquo;.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Name</Label>
            <Input
              value={subAreaName}
              onChange={(e) => setSubAreaName(e.target.value)}
              placeholder="e.g. Salary"
              onKeyDown={(e) => { if (e.key === "Enter" && subAreaName.trim()) handleCreateSubArea(); }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateSubArea(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleCreateSubArea} disabled={saving || !subAreaName.trim()}>
              {saving ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit SubArea */}
      <Dialog open={showEditSubArea} onOpenChange={setShowEditSubArea}>
        <DialogContent className="sm:max-w-[350px]">
          <DialogHeader>
            <DialogTitle>Edit Sub-area</DialogTitle>
            <DialogDescription>Update sub-area name.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Name</Label>
            <Input
              value={subAreaName}
              onChange={(e) => setSubAreaName(e.target.value)}
              placeholder="Sub-area name"
              onKeyDown={(e) => { if (e.key === "Enter" && subAreaName.trim()) handleEditSubArea(); }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditSubArea(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleEditSubArea} disabled={saving || !subAreaName.trim()}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete SubArea */}
      <AlertDialog open={showDeleteSubArea} onOpenChange={setShowDeleteSubArea}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sub-area</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{editingSubArea?.name}&rdquo;?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSubArea}
              disabled={saving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {saving ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Paste Dialogs ──────────────────────────────────────────────── */}

      {/* Paste Areas */}
      <Dialog open={showPasteAreas} onOpenChange={setShowPasteAreas}>
        <DialogContent className="sm:max-w-[540px]">
          <DialogHeader>
            <DialogTitle>Paste Areas</DialogTitle>
            <DialogDescription>
              Paste area names below, one per line. Empty lines are ignored.
              Duplicates will be skipped automatically.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={"Human Resources\nSales\nProduction\nFinance"}
            rows={12}
            className="font-mono text-sm"
          />
          <div className="flex items-center justify-between">
            {pasteText.trim() ? (
              <p className="text-xs text-muted-foreground">
                {parsedLines.length} area{parsedLines.length !== 1 ? "s" : ""} detected
              </p>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowPasteAreas(false)} disabled={pasting}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handlePasteAreas}
                disabled={pasting || parsedLines.length === 0}
              >
                {pasting ? "Creating..." : `Create ${parsedLines.length || ""} Areas`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Paste Sub-areas */}
      <Dialog open={showPasteSubAreas} onOpenChange={setShowPasteSubAreas}>
        <DialogContent className="sm:max-w-[540px]">
          <DialogHeader>
            <DialogTitle>Paste Sub-areas</DialogTitle>
            <DialogDescription>
              Paste sub-area names for &ldquo;{parentAreaForSub?.name}&rdquo;, one per line.
              Duplicates will be skipped automatically.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={"Salaries\nBonuses\nBenefits"}
            rows={12}
            className="font-mono text-sm"
          />
          <div className="flex items-center justify-between">
            {pasteText.trim() ? (
              <p className="text-xs text-muted-foreground">
                {parsedLines.length} sub-area{parsedLines.length !== 1 ? "s" : ""} detected
              </p>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowPasteSubAreas(false)} disabled={pasting}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handlePasteSubAreas}
                disabled={pasting || parsedLines.length === 0}
              >
                {pasting ? "Creating..." : `Create ${parsedLines.length || ""} Sub-areas`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
